// SoundVideoSection.tsx — Settings → Sound & Video.
//
// ═══ DELIBERATELY DECORATIVE — every control here is inert by design. ═══
// TruXSocial has no video player or audio pipeline yet (video is README §10
// deferred), so there is nothing real for these switches to reach. Per the
// platform's inert-code discipline the state is EPHEMERAL (component useState,
// like More-hub QuickSettings) — it is never persisted to prefsStore, so no
// stored value can masquerade as a working setting. The banner says so in the
// UI. When a player lands, promote these to real prefs and wire them.
//
// EXCEPTION — the "Videos" card (2026-09-26) is REAL: the two owner-decided settings
// (Automatic Short Classifier, default on; Autoplay next video, default off) are saved on
// the account (PATCH /users/me/) so they follow the member to the app.
import { useState } from 'react';
import styled from 'styled-components';
import { Panel, Switch, useToast } from '@/ui';
import { useSession } from '@/hooks';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 44ch;
`;

const NoticeBanner = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

interface DecorativeToggle {
  id: string;
  label: string;
  hint: string;
  initial: boolean;
}

const PLAYBACK: DecorativeToggle[] = [
  {
    id: 'autoplay-videos',
    label: 'Autoplay videos',
    hint: 'Play videos automatically as they enter view.',
    initial: true,
  },
  {
    id: 'start-muted',
    label: 'Start videos muted',
    hint: 'Videos begin silent until you turn on sound.',
    initial: true,
  },
  {
    id: 'autoplay-gifs',
    label: 'Autoplay GIFs',
    hint: 'Animate GIFs in the feed without a tap.',
    initial: true,
  },
];

const SOUND: DecorativeToggle[] = [
  {
    id: 'sound-effects',
    label: 'In-app sound effects',
    hint: 'Subtle sounds for actions like posting and refreshing.',
    initial: false,
  },
];

function DecorativeToggleRow({ toggle }: { toggle: DecorativeToggle }) {
  // Ephemeral on purpose — resets on unmount, persisted nowhere.
  const [on, setOn] = useState(toggle.initial);
  return (
    <Row>
      <RowText>
        <RowLabel>{toggle.label}</RowLabel>
        <RowHint>{toggle.hint}</RowHint>
      </RowText>
      <Switch checked={on} onChange={setOn} label={toggle.label} />
    </Row>
  );
}

function VideoSettingsCard() {
  const { currentUser, isAuthenticated, updateProfile } = useSession();
  const toast = useToast();
  if (!isAuthenticated) return null;
  const autoShort = currentUser?.autoShortClassifier ?? true;
  const autoplay = currentUser?.autoplayNext ?? false;
  function save(fields: { autoShortClassifier?: boolean; autoplayNext?: boolean }) {
    void updateProfile(fields).then((ok) => {
      if (!ok) toast('Something went wrong');
    });
  }
  return (
    <Card data-testid="video-settings">
      <CardTitle>Videos</CardTitle>
      <Row>
        <RowText>
          <RowLabel>Automatic Flash Classifier</RowLabel>
          <RowHint>
            When you attach a phone-shaped clip (portrait or square) of 60 seconds or less, start
            with &ldquo;Post as a Flash&rdquo; switched on. You can still switch it on or off for
            any post.
          </RowHint>
        </RowText>
        <Switch
          checked={autoShort}
          onChange={(next) => save({ autoShortClassifier: next })}
          label="Automatic Flash Classifier"
        />
      </Row>
      <Row>
        <RowText>
          <RowLabel>Autoplay next video</RowLabel>
          <RowHint>When a video ends, play the first one in Up Next.</RowHint>
        </RowText>
        <Switch
          checked={autoplay}
          onChange={(next) => save({ autoplayNext: next })}
          label="Autoplay next video"
        />
      </Row>
    </Card>
  );
}

export function SoundVideoSection() {
  return (
    <Stack>
      <VideoSettingsCard />

      <NoticeBanner data-testid="sound-video-preview-note">
        The playback and sound switches below are a preview. They&apos;ll take effect when those
        features ship.
      </NoticeBanner>

      <Card>
        <CardTitle>Playback</CardTitle>
        {PLAYBACK.map((t) => (
          <DecorativeToggleRow key={t.id} toggle={t} />
        ))}
      </Card>

      <Card>
        <CardTitle>Sound</CardTitle>
        {SOUND.map((t) => (
          <DecorativeToggleRow key={t.id} toggle={t} />
        ))}
      </Card>
    </Stack>
  );
}
